import { useState, useEffect, useCallback } from 'react';
import { supabase, handleSupabaseError } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { CompanionPlant } from '../types/plant';

export function useCompanionPlants(plantId: string | undefined) {
  const [companions, setCompanions] = useState<CompanionPlant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchCompanions = useCallback(async () => {
    if (!plantId) {
      setCompanions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // --- Get the current user's garden (so we can filter out plants they already have) ---
      let gardenPlantIds = new Set<string>();
      if (user) {
        try {
          const { data: userGarden, error: gardenError } = await supabase
            .from('user_gardens')
            .select('plant_id')
            .eq('user_id', user.id);

          if (!gardenError && userGarden) {
            gardenPlantIds = new Set(userGarden.map(p => p.plant_id));
          } else if (gardenError) {
            console.warn('Error fetching user garden:', gardenError);
          }
        } catch (err) {
          console.warn('Error fetching user garden (ignored):', err);
        }
      }

      // --- Forward companion relationships ---
      const { data: forwardData, error: forwardErr } = await supabase
        .from('companion_plants')
        .select(`
          id,
          benefits,
          companion_plant:plants!companion_plants_companion_plant_id_fkey ( id, name )
        `)
        .eq('plant_id', plantId);

      // --- Reverse companion relationships ---
      const { data: reverseData, error: reverseErr } = await supabase
        .from('companion_plants')
        .select(`
          id,
          benefits,
          plant:plants!companion_plants_plant_id_fkey ( id, name )
        `)
        .eq('companion_plant_id', plantId);

      if (forwardErr) {
        console.error('Forward companion query error:', forwardErr);
        throw forwardErr;
      }
      if (reverseErr) {
        console.error('Reverse companion query error:', reverseErr);
        throw reverseErr;
      }

      const all = [...(forwardData || []), ...(reverseData || [])];

      // Filter out plants already in the user's garden
      const filtered = user
        ? all.filter(c => {
            const cpId = c.companion_plant?.id || c.plant?.id;
            return cpId && !gardenPlantIds.has(cpId);
          })
        : all;

      setCompanions(filtered);
    } catch (err) {
      const errorMessage = handleSupabaseError(err);
      console.error('Error fetching companion plants:', err);
      setError(errorMessage);
      setCompanions([]);
    } finally {
      setLoading(false);
    }
  }, [plantId, user]);

  useEffect(() => {
    fetchCompanions();
  }, [fetchCompanions]);

  return {
    companions,
    loading,
    error,
    retry: fetchCompanions
  };
}
